import React from "react";
import styles from "./CustomTable.module.scss";

interface Column<T = Record<string, unknown>> {
  key: string;
  title: React.ReactNode;
  width?: string;
  align?: "left" | "right" | "center";
  render?: (value: unknown, record: T, index: number) => React.ReactNode;
}

interface CustomTableProps<T = Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
}

const CustomTable = <T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  loadingComponent,
  className = "",
  headerClassName = "",
  bodyClassName = "",
}: CustomTableProps<T>) => {
  const renderCell = (
    column: Column<T>,
    record: T,
    index: number
  ): React.ReactNode => {
    if (column.render) {
      return column.render(record[column.key], record, index);
    }
    return String(record[column.key] ?? "");
  };

  return (
    <div className={`${styles.table} ${className}`}>
      {/* Header */}
      <div className={`${styles.table_header} ${headerClassName}`}>
        {columns.map((column) => (
          <div
            key={column.key}
            className={styles.header_cell}
            style={{
              width: column.width,
              textAlign: column.align || "right",
            }}>
            {column.title}
          </div>
        ))}
      </div>

      {/* Body */}
      {loading ? (
        loadingComponent
      ) : (
        <div className={`${styles.table_body} ${bodyClassName}`}>
          {data?.map((record, rowIndex) => (
            <div key={rowIndex} className={styles.table_row}>
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={styles.table_cell}
                  style={{
                    width: column.width,
                    textAlign: column.align || "right",
                  }}>
                  {renderCell(column, record, rowIndex)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomTable;
